import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';

import { TrackedSession } from '../../../../../types/common.types';
import { AttendanceSession, AttendanceSessionTemplate } from '../../../../../types/entity.types';

import { isCheckpointConfigured } from '../../../../../utils/accountability.util';
import { ARCHIVED, LONG_MONTHS } from '../../../../../utils/constants';
import { convertDateToTimeString, getClassNames } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import AccountabilityAttendanceSessionsModal from './accountability-attendance-sessions-modal/AccountabilityAttendanceSessionsModal';
import AccountabilityCheckpointsModal from './accountability-checkpoints-modal/AccountabilityCheckpointsModal';
import AccountabilityTrackedSessionModal from './accountability-tracked-session-modal/AccountabilityTrackedSessionModal';

import AccountabilityTrackedSessionDashboardContainer from '../../../../../containers/programme/accountability/accountability-sessions/accountability-tracked-session-dashboard/AccountabilityTrackedSessionDashboard.container';

import { ReactComponent as DeleteIcon } from '../../../../../resources/icons/close.svg';
import { ReactComponent as DownloadIcon } from '../../../../../resources/icons/upload.svg';

import '../accountability.scss';
import './accountability-sessions.scss';

type Props = {
  status: string;
  attendanceSessions: AttendanceSession[];
  trackedSessions: TrackedSession[];
  updateAttendanceSession: (session: Partial<AttendanceSession>) => void;
  deleteAttendanceSession: (externalReference: string) => void;
  generateAttendanceSessions: (sessionTemplate: AttendanceSessionTemplate, onSuccess: () => void) => void;
  createTrackedSession: (session: TrackedSession) => void;
  updateTrackedSession: (session: TrackedSession) => void;
  deleteTrackedSession: (uuid: string) => void;
  downloadTrackedSessionDashboardExcel: (sessionEventExternalReference: string) => void;
};

const AccountabilitySessions = ({
  status,
  attendanceSessions = [],
  trackedSessions = [],
  updateAttendanceSession,
  deleteAttendanceSession,
  generateAttendanceSessions,
  createTrackedSession,
  updateTrackedSession,
  deleteTrackedSession,
  downloadTrackedSessionDashboardExcel
}: Props) => {
  const [open, setOpen] = useState(true);
  const [selectedAttendanceSession, setSelectedAttendanceSession] = useState<AttendanceSession>();
  const [showAttendanceSessionModal, setShowAttendanceSessionModal] = useState(false);
  const [selectedTrackedSession, setSelectedTrackedSession] = useState<TrackedSession>();
  const [showTrackedSessionModal, setShowTrackedSessionModal] = useState(false);
  const [showTrackedSessionCheckpointsModal, setShowTrackedSessionCheckpointsModal] = useState(false);
  const [sessionEventExternalReference, setSessionEventExternalReference] = useState<string>();

  const handleAddAttendanceSessionClick = () => {
    setSelectedAttendanceSession(undefined);
    setShowAttendanceSessionModal(true);
  };

  const handleAttendanceSessionClick = (session: AttendanceSession) => {
    setSelectedAttendanceSession(session);
    setShowAttendanceSessionModal(true);
  };

  const handleDeleteAttendanceSessionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    session: AttendanceSession
  ) => {
    event.stopPropagation();

    deleteAttendanceSession(session.externalReference as string);
  };

  const handleAddTrackedSessionClick = () => {
    setSelectedTrackedSession(undefined);
    setShowTrackedSessionModal(true);
  };

  const handleTrackedSessionClick = (session: TrackedSession) => {
    setSelectedTrackedSession(session);
    setShowTrackedSessionModal(true);
  };

  const handleDeleteTrackedSessionClick = (session: TrackedSession) => {
    deleteTrackedSession(session.uuid);
  };

  const handleConfigureTrackedSessionClick = (session: TrackedSession) => {
    setSelectedTrackedSession(session);
    setShowTrackedSessionCheckpointsModal(true);
  };

  const disabled = status === ARCHIVED;

  const renderLabels = () => (
    <div className="accountability-sessions__labels">
      <p className="accountability-sessions__label">Session Name</p>
      <p className="accountability-sessions__label">Date / Time</p>
      <span />
    </div>
  );

  const renderAttendanceSessions = () => {
    const copyOfSessions = [...attendanceSessions];
    const sortedSessions = copyOfSessions.sort((sessionOne, sessionTwo) => {
      const dateOne = sessionOne.startDate;
      const dateTwo = sessionTwo.startDate;

      if (dateOne > dateTwo) return 1;
      if (dateOne < dateTwo) return -1;
      return 0;
    });

    return sortedSessions.map((session, index) => (
      <div className="accountability-sessions__session" key={index} data-testid="session">
        <button
          className={getClassNames('accountability-sessions__session__block', { disabled })}
          onClick={() => handleAttendanceSessionClick(session)}
        >
          {session.name}
        </button>
        <button
          className={getClassNames('accountability-sessions__session__block', { disabled })}
          onClick={() => handleAttendanceSessionClick(session)}
        >
          {formatSessionDate(session)}
        </button>
        <div className="accountability-sessions__session__actions">
          {!disabled && (
            <button
              className="accountability-sessions__delete-button"
              onClick={(event) => handleDeleteAttendanceSessionClick(event, session)}
            >
              <DeleteIcon data-testid="delete-icon" />
            </button>
          )}
        </div>
      </div>
    ));
  };

  const renderTrackedSessions = () =>
    trackedSessions.map((session, index) => (
      <div className="accountability-sessions__session" key={index} data-testid="session">
        <button
          className={getClassNames('accountability-sessions__session__block', { disabled })}
          onClick={() => handleTrackedSessionClick(session)}
        >
          {session.name}
        </button>
        <button
          className={getClassNames('accountability-sessions__session__block', { disabled })}
          onClick={() => handleTrackedSessionClick(session)}
        >
          {formatSessionDate(session)}
        </button>

        <div className="accountability-sessions__session__actions">
          {!disabled && (
            <button
              className="accountability-sessions__delete-button"
              onClick={() => handleDeleteTrackedSessionClick(session)}
            >
              <DeleteIcon data-testid="delete-icon" />
            </button>
          )}
          <Button
            className="accountability-sessions__configure-button"
            text={status === ARCHIVED ? 'View' : 'Configure'}
            onClick={() => handleConfigureTrackedSessionClick(session)}
          />
          <div
            className={getClassNames('accountability-sessions__tracked-session-status', {
              incomplete: session.checkpoints.length > 0,
              complete:
                session.checkpoints.length > 0 &&
                session.checkpoints.every((checkpoint) => isCheckpointConfigured(checkpoint))
            })}
          />
          {!session.isNew && (
            <>
              <Button
                className="accountability-sessions__dashboard-button"
                text="Dashboard"
                onClick={() => setSessionEventExternalReference(session.uuid)}
              />
              <button
                className="accountability-sessions__download-button"
                onClick={() => downloadTrackedSessionDashboardExcel(session.uuid)}
              >
                <DownloadIcon />
              </button>
            </>
          )}
        </div>
      </div>
    ));

  return (
    <div
      className={getClassNames('accountability__section', {
        closed: !open
      })}
      data-testid="accountability-sessions"
    >
      <div className="accountability__section__header">
        <p>SESSIONS</p>
        {!open && (
          <FontAwesomeIcon
            className="accountability__section__open-icon"
            icon={faPlus}
            onClick={() => setOpen(true)}
            data-testid="open-icon"
          />
        )}
        {open && (
          <FontAwesomeIcon
            className="accountability__section__close-icon"
            icon={faMinus}
            onClick={() => setOpen(false)}
            data-testid="close-icon"
          />
        )}
      </div>
      {open && (
        <div className="accountability-sessions">
          <div className="accountability-sessions__section">
            <p className="accountability-sessions__section__header">Attendance Sessions</p>
            {attendanceSessions.length > 0 && (
              <>
                {renderLabels()}
                {renderAttendanceSessions()}
              </>
            )}
            {!disabled && (
              <Button
                className="accountability-sessions__add-button"
                text="Add"
                onClick={handleAddAttendanceSessionClick}
              />
            )}
          </div>

          <div className="accountability-sessions__section">
            <p className="accountability-sessions__section__header">Tracked Sessions</p>
            {trackedSessions.length > 0 && (
              <>
                {renderLabels()}
                {renderTrackedSessions()}
              </>
            )}
            {!disabled && (
              <Button
                className="accountability-sessions__add-button"
                text="Add"
                onClick={handleAddTrackedSessionClick}
              />
            )}
          </div>
        </div>
      )}

      <AccountabilityAttendanceSessionsModal
        status={status}
        show={showAttendanceSessionModal}
        close={() => setShowAttendanceSessionModal(false)}
        session={selectedAttendanceSession}
        updateSession={updateAttendanceSession}
        generateSessions={generateAttendanceSessions}
      />

      <AccountabilityTrackedSessionModal
        status={status}
        show={showTrackedSessionModal}
        close={() => setShowTrackedSessionModal(false)}
        session={selectedTrackedSession}
        createSession={createTrackedSession}
        updateSession={updateTrackedSession}
      />

      <AccountabilityCheckpointsModal
        status={status}
        show={showTrackedSessionCheckpointsModal}
        close={() => setShowTrackedSessionCheckpointsModal(false)}
        session={selectedTrackedSession}
        updateSession={updateTrackedSession}
      />

      <AccountabilityTrackedSessionDashboardContainer
        sessionEventExternalReference={sessionEventExternalReference}
        onClose={() => setSessionEventExternalReference(undefined)}
      />
    </div>
  );
};

const formatSessionDate = (session: AttendanceSession | TrackedSession) => {
  if (!session.startDate) return '-';

  const date = new Date(session.startDate);

  const day = date.getUTCDate();
  const month = LONG_MONTHS[date.getUTCMonth()] as string;
  const year = date.getUTCFullYear();
  const startTime = convertDateToTimeString(session.startTime ?? '');
  const endTime = convertDateToTimeString(session.endTime ?? '');

  return `${day} ${month} ${year} ${startTime} - ${endTime}`;
};

export default AccountabilitySessions;
